import React, { useCallback, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import types from "prop-types"
import { conversationShape } from "utils/propTypeShapes"
import { markConversationMessagesAsRead } from "reduxSlices/formSubmissionConversationsSlice"
import { useHasMounted } from "hooks"
import ItemRow from "shared/ItemRow"
import ProfileIcon from "shared/icons/ProfileIcon"
import TruncatedWithTooltip from "shared/TruncatedWithTooltip"
import ConversationActions from "./ConversationActions"
import ConversationClosedIndicator from "./ConversationClosedIndicator"
import ConversationMessageForm from "./ConversationMessageForm"
import ConversationMessageList from "./ConversationMessageList"
import ConversationUnreadMessagesIndicator from "./ConversationUnreadMessagesIndicator"

const Conversation = ({
  canAddMessages = false, canTakeActions = false, conversation, text = "",
}) => {
  const itemRowRef = useRef()
  const { hasMounted } = useHasMounted()
  const dispatch = useDispatch()

  const {
    id, closed, messages, opener, title,
  } = conversation

  const collapseConversationAndScrollIntoView = useCallback(() => {
    itemRowRef.current?.collapse()
    // Allow time for the item row to collapse before scrolling.
    setTimeout(() => itemRowRef.current?.scrollIntoView(), 10)
  }, [])

  useEffect(() => {
    if (hasMounted && closed) collapseConversationAndScrollIntoView()
  }, [closed, collapseConversationAndScrollIntoView, hasMounted])

  const markMessagesAsRead = useCallback(() => {
    dispatch(markConversationMessagesAsRead({ conversationId: id }))
  }, [dispatch, id])

  return (
    <ItemRow
      ref={itemRowRef}
      collapsable
      onExpand={markMessagesAsRead}
      itemRowHeaderContent={(
        <div className="flex flex-row items-center gap-4 w-full">
          <div className="flex flex-row items-center gap-2 grow min-w-0">
            <ConversationClosedIndicator closed={closed} />
            <TruncatedWithTooltip text={title} />
          </div>
          <div className="flex flex-row items-center gap-6 shrink-0">
            <ConversationUnreadMessagesIndicator className="h-8" conversationId={id} />
            <ProfileIcon user={opener} />
          </div>
        </div>
      )}
      itemRowHeaderContentClassName="min-w-0"
      itemRowBody={(
        <div className="flex flex-col gap-8">
          {
            text && (
              <p className="text-xs text-dark">
                {text}
              </p>
            )
          }
          {
            canAddMessages && (
              <ConversationMessageForm conversationId={id} />
            )
          }
          <ConversationMessageList messages={messages} />
          {
            canTakeActions && (
              <ConversationActions conversation={conversation} />
            )
          }
        </div>
      )}
    />
  )
}

Conversation.propTypes = {
  canAddMessages: types.bool,
  canTakeActions: types.bool,
  conversation: conversationShape.isRequired,
  text: types.string,
}

export default Conversation
